import React, { useState } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { AlertCircle, Eye, EyeOff, CreditCard } from 'lucide-react';
import AppLayout from '../components/AppLayout';

const LoginPage = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [params] = useSearchParams();
  const nextPath = params.get('next');
  const reason = params.get('reason');
  
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const data = await login(email, password);
      
      // Honor ?next= for post-login redirect (e.g. coming from email Update Card link)
      if (nextPath && nextPath.startsWith('/')) {
        navigate(nextPath);
        return;
      }
      // Users mid-subscription (payment failing but still recoverable) land on the
      // dashboard so they can see the SubscriptionBanner and update their card.
      // Canceled users are treated like new signups — they go to /subscribe to
      // pick any plan (no locked-in previous plan).
      const status = data.user.subscription_status;
      const statusesWithRealSub = ['active', 'canceling', 'past_due', 'suspended'];
      if (statusesWithRealSub.includes(status)) {
        navigate('/dashboard');
      } else if (status === 'canceled') {
        navigate('/subscribe');
      } else {
        navigate('/checkout?plan=' + data.user.plan_id);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AppLayout>
      <div className="min-h-[calc(100vh-200px)] bg-gradient-to-br from-rose-50 via-white to-orange-50 flex items-center justify-center px-4 py-12">
        <div className="bg-white rounded-2xl shadow-xl p-8 w-full max-w-md">
          <h1 className="text-2xl font-bold text-gray-800 mb-2">Welcome Back</h1>
          <p className="text-gray-600 mb-8">Sign in to your VirtualFit account</p>

          {(reason === 'update_card' || reason === 'session_expired') && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6 flex items-start" data-testid="login-reason-banner">
              <CreditCard className="w-5 h-5 text-amber-600 mr-3 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-amber-800">
                {reason === 'update_card'
                  ? 'Log in to continue updating your payment method.'
                  : 'Your session expired. Log in again to continue.'}
              </p>
            </div>
          )}

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-6 flex items-start">
              <AlertCircle className="w-5 h-5 text-red-500 mr-3 flex-shrink-0 mt-0.5" />
              <p className="text-red-700">{error}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-rose-500 focus:border-transparent"
                placeholder="you@example.com"
              />
            </div>

            <div>
              <div className="flex justify-between items-center mb-2">
                <label className="block text-sm font-medium text-gray-700">Password</label>
                <Link to="/forgot-password" className="text-sm text-rose-500 hover:text-rose-600">
                  Forgot password?
                </Link>
              </div>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-rose-500 focus:border-transparent pr-12"
                  placeholder="••••••••"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full py-4 bg-gradient-to-r from-rose-500 to-orange-500 text-white rounded-xl font-semibold hover:shadow-lg transition-all disabled:opacity-50"
            >
              {loading ? 'Signing in...' : 'Sign In'}
            </button>
          </form>

          <p className="text-center text-gray-600 mt-6">
            Don't have an account?{' '}
            <Link to="/subscribe" className="text-rose-500 hover:text-rose-600 font-medium">
              Sign up
            </Link>
          </p>
        </div>
      </div>
    </AppLayout>
  );
};

export default LoginPage;
